import listener from '@mailobj-browser/front/js/events/listeners/listener.js'
import preventDefault from '@mailobj-browser/front/js/events/hooks/preventDefault.js'
import stopImmediatePropagation from '@mailobj-browser/front/js/events/hooks/stopImmediatePropagation.js'
import capture from '@mailobj-browser/front/js/events/options/capture.js'
import once from '@mailobj-browser/front/js/events/options/once.js'
import dragEnd from '@mailobj-browser/front/js/events/types/dragEnd.js'
import dragEnter from '@mailobj-browser/front/js/events/types/dragEnter.js'
import dragLeave from '@mailobj-browser/front/js/events/types/dragLeave.js'
import dragOver from '@mailobj-browser/front/js/events/types/dragOver.js'
import drop from '@mailobj-browser/front/js/events/types/drop.js'
import one from '@mailobj-browser/front/js/selectors/one.js'
import removed from '@mailobj-browser/front/js/tree/removed.js'
import array from '@mailobj-browser/front/js/utils/array.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import { list } from '#utilities/js/fs/field.js'
import { modifiers } from '../styles.js'
import * as styles from '../xfiles/styles.js'
import { handle } from './uploader.js'

const dropZones = new WeakMap()

const bubbling = object(null, {
  bubbles: true
})

const noDataTransfer = object()

const onDragEnd = object(listener, {
  type: dragEnd,
  capture,
  hooks: array([
    preventDefault,
    stopImmediatePropagation
  ]),
  task: document => {
    const dropZone = dropZones.get(document)
    
    dropZone.classList.remove(modifiers.drop.droppable)
    onDragLeave.forget(dropZone)
    onDragOver.forget(dropZone)
    onDrop.forget(dropZone)
  }
})

const onDragEnter = object(listener, {
  type: dragEnter,
  capture,
  hooks: array([
    preventDefault,
    stopImmediatePropagation
  ]),
  task: (document, event) => {
    const { dataTransfer } = event
    const { types } = dataTransfer ?? noDataTransfer
    
    if (!types?.includes('Files')) {
      return
    }
    
    const dropZone = dropZones.get(document)
    
    dropZone.classList.add(modifiers.drop.droppable)
    onDragLeave.listen(dropZone)
    onDragOver.listen(dropZone)
    onDrop.listen(dropZone)
  }
})

const onDragLeave = object(listener, {
  type: dragLeave,
  capture,
  hooks: array([
    preventDefault,
    stopImmediatePropagation
  ]),
  task: dropZone => {
    dropZone.classList.add(modifiers.drop.hover)
  }
})

const onDragOver = object(listener, {
  type: dragOver,
  capture,
  once,
  hooks: array([
    preventDefault,
    stopImmediatePropagation
  ]),
  task: dropZone => {
    dropZone.classList.add(modifiers.drop.hover)
  }
})

const onDrop = object(listener, {
  type: drop,
  capture,
  once,
  hooks: array([
    preventDefault,
    stopImmediatePropagation
  ]),
  task: async (dropZone, event) => {
    const input = one('[type="file"]', dropZone)
    const { ownerDocument } = input
    const { defaultView } = ownerDocument
    const { InputEvent } = defaultView
    const { dataTransfer } = event
    const { files } = dataTransfer
    
    input.files = files
    input.dispatchEvent(new InputEvent('input', bubbling))
    onDragEnd.listen(ownerDocument)
    onDragEnter.listen(ownerDocument)
    onDragLeave.forget(dropZone)
    onDragOver.forget(dropZone)
    onDrop.forget(dropZone)
  }
})

const handler = async (
  input,
  uploads
) => {
  await list(input, uploads, styles)
}

export default dropZone => {
  const { ownerDocument } = dropZone
  const input = one('[type="file"]', dropZone)
  
  handle(input, handler)
  dropZones.set(ownerDocument, dropZone)
  onDragEnd.listen(ownerDocument)
  onDragEnter.listen(ownerDocument)
  
  queueMicrotask(async () => {
    await removed(dropZone)
    dropZones.delete(ownerDocument)
    onDragEnd.forget(ownerDocument)
    onDragEnter.forget(ownerDocument)
  })
}
